import { analyticsTrack } from "../utils/analytics";
import { analyticsReady } from "../utils/ready";
import { logger } from "./logger";

type Variables = Record<string, never>;

function warnNotInstalled(reason: string) {
  logger.warn(`${reason}, skipping Drift conversion tracking`);
}

function shouldIgnoreTracker(tracker: UniversalAnalytics.Tracker) {
  const trackerName = tracker?.get("name");

  return trackerName.indexOf("gtm") !== 0;
}

function driftEvent(
  event: { [k: string]: unknown },
  props: { eventAction: string }
) {
  return {
    eventCategory: "Drift Widget",
    eventLabel: `Playbook ID: ${event?.playbookId}`,
    ...props,
  };
}

function eachTracker(cb: (tracker: UniversalAnalytics.Tracker) => void) {
  if (window.ga == null) {
    warnNotInstalled("GA not found in window");
    return;
  }

  ga((_defaultTracker) => {
    const trackers = ga.getAll();

    for (const tracker of trackers) {
      if (shouldIgnoreTracker(tracker)) {
        cb(tracker);
      }
    }
  });
}

function handleEvent(eventAction: string, analyticsEventName: string) {
  return (event: { [k: string]: unknown }) => {
    eachTracker((tracker) => {
      try {
        tracker.send("event", driftEvent(event, { eventAction }));
      } catch (e) {
        logger.error("Could not send event to a GA tracker", e);
      }
    });

    try {
      analyticsReady().then((floorlytics) => {
        if (event?.email) {
          floorlytics.identify(
            { email: event?.email },
            window.analytics._defaultContext
          );
        }

        if (event?.phone) {
          floorlytics.identify(
            { phoneNumber: event?.phone },
            window.analytics._defaultContext
          );
        }
      });

      analyticsTrack(analyticsEventName, {
        application: "drift",
        category: "chat",
        driftEvent: event,
      });
    } catch (e) {
      logger.error("Could not send event global window.analytics tracker", e);
    }
  };
}

export function install(_vars: Variables): void {
  const drift = window["drift"];

  if (typeof drift === "undefined" || typeof drift["on"] !== "function") {
    warnNotInstalled("Drift not found in window");
    return;
  }

  drift.on("ready", (_api) => {
    drift.on("startConversation", handleEvent("Chat Started", "Chat Started"));

    drift.on("message:sent", handleEvent("Message Sent", "Chat Message Sent"));

    drift.on(
      "message",
      handleEvent("Message Received", "Chat Message Received")
    );

    drift.on(
      "emailCapture",
      handleEvent("Email Captured", "Email Captured in Chat")
    );

    drift.on(
      "phoneCapture",
      handleEvent("Phone Number Captured", "Phone Number Captured in Chat")
    );

    drift.on(
      "scheduling:meetingBooked",
      handleEvent("Meeting Booked", "Meeting Booked in Chat")
    );

    drift.on(
      "conversation:playbookFired",
      handleEvent("Playbook Fired", "Chat Playbook Fired")
    );

    drift.on(
      "conversation:playbookClicked",
      handleEvent("Playbook Clicked", "Chat Playbook Clicked")
    );

    drift.on(
      "conversation:ButtonClicked",
      handleEvent("Button Clicked", "Button Clicked in Chat")
    );

    drift.on(
      "conversation:playbookDismissed",
      handleEvent("Playbook Dismissed", "Chat Playbook Dismissed")
    );

    drift.on(
      "conversation:firstInteraction",
      handleEvent("First Interaction", "First Chat Interaction")
    );
  });
}
